import { PriceUtils } from '@wix/bookings-uou-mappers';
import { useTranslation } from '@wix/yoshi-flow-editor';
import React from 'react';
import { Text, TextPriority } from 'wix-ui-tpa/cssVars';
import { SelectedAddons, SlotAddOn } from '../../../../../../utils/state/types';
import { useFormComponentContext } from '../../../../Hooks/useFormComponentContext';
import { classes } from '../../AddonsDetails.st.css';
import { AddonsDetailsDataHooks } from '../../datahooks';
import { AddonCard } from '../AddonCard';

export const GroupAddons = ({
  name,
  maxNumberOfAddOns,
  addOns,
  selectedAddons,
  onAddonSelect,
  onAddonQuantityChange,
  prompt,
  groupId,
}: {
  name: string;
  maxNumberOfAddOns?: number;
  addOns?: SlotAddOn[];
  selectedAddons: SelectedAddons;
  onAddonSelect: (addonId: string, isSelected: boolean) => void;
  onAddonQuantityChange: (addonId: string, quantity: number) => void;
  prompt?: string;
  groupId: string;
}) => {
  const { t } = useTranslation();
  const { dateRegionalSettingsLocale } = useFormComponentContext();

  const groupSelectedAddons = selectedAddons[groupId] || {};
  // Sums across all groups, but CLASS/COURSE services only ever have one, so this equals the group's own count (matches onAddonSelect's cap check).
  const totalSelectedAddonsCount = Object.keys(selectedAddons).reduce(
    (count, gId) => count + Object.keys(selectedAddons[gId]).length,
    0,
  );
  const isGroupLimitReached =
    maxNumberOfAddOns !== undefined &&
    totalSelectedAddonsCount >= maxNumberOfAddOns;

  return (
    <>
      <Text
        className={classes.groupName}
        data-hook={AddonsDetailsDataHooks.GROUP_NAME}
      >
        {name}
      </Text>
      {prompt && (
        <Text
          className={classes.groupSubTitle}
          data-hook={AddonsDetailsDataHooks.GROUP_PROMPT}
        >
          {prompt}
        </Text>
      )}
      {maxNumberOfAddOns && (
        <Text
          priority={TextPriority.secondary}
          className={classes.groupLimitation}
        >
          {t('app.bookings-form.add-ons.group.max-limit', {
            maxQuantity: maxNumberOfAddOns,
          })}
        </Text>
      )}
      <div className={classes.addonsContainer}>
        {addOns?.map((addOn, addonIndex) => {
          const selectedAddon = groupSelectedAddons[addOn.id!];
          return (
            <AddonCard
              key={addOn.id}
              index={addonIndex}
              name={addOn.name!}
              image={addOn.image}
              price={
                addOn.price && addOn.price !== '0'
                  ? t('app.bookings-form.add-ons.plus.price', {
                      price: PriceUtils.getFormattedCurrency({
                        price: Number(addOn.price),
                        currency: addOn.currency!,
                        locale: dateRegionalSettingsLocale,
                        currencyDisplay: 'symbol',
                      }),
                    })
                  : undefined
              }
              quantity={selectedAddon?.quantity ?? 0}
              isSelected={!!selectedAddon}
              maxQuantity={addOn.maxQuantity ?? 1}
              isDisabled={!selectedAddon && isGroupLimitReached}
              groupMaxAddOns={maxNumberOfAddOns}
              onAdd={() => onAddonSelect(addOn.id!, true)}
              onRemove={() => onAddonSelect(addOn.id!, false)}
              onQuantityChange={(quantity) =>
                onAddonQuantityChange(addOn.id!, quantity)
              }
            />
          );
        })}
      </div>
    </>
  );
};
